/**
 * Recording money from a customer.
 *
 * The rules live here rather than on the two screens that collect a payment,
 * because a payment refused by the API after the clerk has closed the till
 * drawer is a payment argued about at the counter. These mirror
 * `backend/services/payment_service.py` — the API is still the authority; this
 * is only so the answer comes back before the request does.
 */
import api from '@/lib/api';

export const PAYMENT_MODES = ['cash', 'upi', 'bank_transfer', 'cheque', 'card'];

// a cheque or a transfer with nothing to quote cannot be matched to the bank
// statement, so the number is asked for while it is still in the clerk's hand
export const REFERENCE_REQUIRED_MODES = ['cheque', 'bank_transfer'];

export const MAX_PAYMENT_AMOUNT = 100000000;

// rupees are floats on both sides of the wire; a half-paisa is not a shortfall
const EPSILON = 0.005;

export const ON_ACCOUNT = 'none';

export const modeLabel = (mode) => (mode || '').replace(/_/g, ' ');

export const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

export const balanceOf = (order) => (order?.order_value || 0) - (order?.total_paid || 0);

export const EMPTY_PAYMENT = {
  order_id: ON_ACCOUNT,
  amount: '',
  mode: 'cash',
  reference_number: '',
  remarks: '',
};

/**
 * What is wrong with this form, in the words the clerk needs, or null.
 * `orders` is the customer's outstanding list, used to check the chosen bill.
 */
export function paymentError(form, orders = []) {
  const amount = Number(form?.amount);
  if (form?.amount === '' || form?.amount === null || form?.amount === undefined) {
    return 'Enter the amount received';
  }
  if (!Number.isFinite(amount)) return 'Amount must be a number';
  if (amount <= 0) return 'Amount must be greater than zero';
  if (amount > MAX_PAYMENT_AMOUNT) return `Amount cannot exceed ${money(MAX_PAYMENT_AMOUNT)}`;

  if (!PAYMENT_MODES.includes(form?.mode)) return 'Choose how the payment was made';
  if (REFERENCE_REQUIRED_MODES.includes(form.mode) && !form.reference_number?.trim()) {
    return form.mode === 'cheque'
      ? 'Cheque number is required'
      : 'Transaction reference is required';
  }

  if (form.order_id && form.order_id !== ON_ACCOUNT) {
    const order = orders.find((o) => o.id === form.order_id);
    const balance = balanceOf(order);
    if (order && balance <= EPSILON) return `${order.order_id} is already fully paid`;
    if (order && amount - balance > EPSILON) {
      return `${money(amount)} is more than the ${money(balance)} outstanding on ${order.order_id}. `
        + 'Record it on account to spread it across their orders.';
    }
  }
  return null;
}

/**
 * What the clerk should know before pressing the button — not an error.
 */
export function paymentNote(form, { outstanding } = {}) {
  const amount = Number(form?.amount);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if (form?.order_id && form.order_id !== ON_ACCOUNT) return null;
  // a desk that may not see the lifetime ledger is told nothing rather than nil
  if (outstanding === null || outstanding === undefined) return null;
  if (outstanding <= EPSILON) return `Nothing is outstanding — ${money(amount)} will be held as advance credit.`;
  if (amount - outstanding > EPSILON) {
    return `${money(outstanding)} settles every open order; ${money(amount - outstanding)} will be held as advance credit.`;
  }
  return 'Applied to the oldest open orders first.';
}

export function recordCustomerPayment(customerId, form) {
  const payload = {
    amount: Number(form.amount),
    mode: form.mode,
    reference_number: (form.reference_number || '').trim(),
    remarks: (form.remarks || '').trim(),
  };
  if (form.order_id && form.order_id !== ON_ACCOUNT) payload.order_id = form.order_id;
  return api.post(`/customers/${customerId}/payments`, payload);
}
